import "./App.css";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import FishPriceComparison from "./FishPriceComparison";
import React, { useEffect, Suspense } from "react";
import { DataProvider } from "./contexts/DataContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import RouteSEO from "./contexts/RouteSEOContext";
import Navbar from "./components/Navbar";
import PageViewTracker from "./components/PageViewTracker";
import ReferralTracker from "./components/ReferralTracker";
import GiveawayAnnouncement from "./components/GiveawayAnnouncement";

// Lazy-load routes that are not on the critical path
const InvertPriceComparison = React.lazy(() => import("./InvertPriceComparison"));
const PlantPriceComparison = React.lazy(() => import("./PlantPriceComparison"));
const SuppliesPriceComparison = React.lazy(() => import("./SuppliesPriceComparison"));
const Admin = React.lazy(() => import("./Admin"));
const NotFound = React.lazy(() => import("./NotFound"));
const Newsletter = React.lazy(() => import("./Newsletter"));
const Reviews = React.lazy(() => import("./Reviews"));
const Notifications = React.lazy(() => import("./Notifications"));
const Classifieds = React.lazy(() => import("./Classifieds"));
const OneTapHandler = React.lazy(() => import("./components/OneTapHandler"));
const FeedbackPopup = React.lazy(() => import("./components/FeedbackPopup"));
const ReefWizard = React.lazy(() => import("./ReefWizard"));
const TankForm = React.lazy(() => import("./TankForm"));
const TankDisplay = React.lazy(() => import("./TankDisplay"));
const ReefWizardChat = React.lazy(() => import("./ReefWizardChat"));
const Social = React.lazy(() => import("./Social"));
const Videos = React.lazy(() => import("./Videos"));
const Articles = React.lazy(() => import("./Articles"));
const Reefcyclopedia = React.lazy(() => import("./Reefcyclopedia"));
const ShoppingAssistant = React.lazy(() => import("./ShoppingAssistant"));
const SignIn = React.lazy(() => import("./SignIn"));
const TermsAndConditions = React.lazy(() => import("./TermsAndConditions"));
const PrivacyPolicy = React.lazy(() => import("./PrivacyPolicy"));
const GiveawayRules = React.lazy(() => import("./GiveawayRules"));

function AppleAuthHandler() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) return; // Already logged in

    const urlParams = new URLSearchParams(location.search);
    const appleAuthToken = urlParams.get('apple_auth_token');
    const appleAuthUser = urlParams.get('apple_auth_user');
    const authError = urlParams.get('auth_error');

    if (authError) {
      console.error('Apple authentication error:', authError);
      // Clean up URL
      navigate(location.pathname, { replace: true });
      return;
    }

    if (appleAuthToken && appleAuthUser) {
      try {
        // Parse user data
        const user = JSON.parse(appleAuthUser);
        
        // Login with the token
        login(appleAuthToken, user);
        
        // Get redirect path from localStorage
        const redirectPath = localStorage.getItem('apple_auth_redirect');
        
        // Clean up localStorage
        localStorage.removeItem('apple_auth_redirect');
        
        // Navigate to the stored path or home
        if (redirectPath && redirectPath !== '/') {
          navigate(redirectPath, { replace: true });
        } else {
          // Clean up URL params and stay on home
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Failed to process Apple authentication:', error);
        // Clean up URL
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.search, login, navigate, location.pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const showGiveawayBanner = location.pathname !== "/giveaway-rules";

  return (
    <div className="App">
      <Suspense fallback={null}>
        <OneTapHandler />
      </Suspense>
      <AppleAuthHandler />
      <PageViewTracker />
      <ReferralTracker />
      <Suspense fallback={null}>
        <FeedbackPopup />
      </Suspense>
      <div className="p-4 max-w-7xl mx-auto">
        <div
          className="logo-box bg-indigo-50 mb-4 h-40 flex flex-col justify-center items-center logo-container rounded-lg shadow"
          fetchPriority="high"
          style={{
            backgroundImage: `url(/hero-optimized.webp)`,
          }}
        >
          <div className="relative logo-inner-container">
            <img
              src="/logo-optimized.webp"
              alt="Logo"
              className="h-32 bubble-image logo-image logo-image-smaller-on-mobile"
              fetchPriority="high"
            />
            <h1 className="font-bold bubble-text text-center">
              <b className="text-2xl">Sea What You Save</b>{" "}
              <p>Cast A Line On Every Site At The Same Time!</p>
            </h1>
          </div>
        </div>
        <Navbar />
        
        {showGiveawayBanner && <GiveawayAnnouncement />}
        
        <div className="routes-container">
          <Suspense fallback={<div className="flex justify-center items-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
            <Routes>
              <Route path="/" element={<FishPriceComparison />} />
              <Route path="/inverts" element={<InvertPriceComparison />} />
              <Route path="/plants" element={<PlantPriceComparison />} />
              <Route path="/supplies" element={<SuppliesPriceComparison />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/newsletter" element={<Newsletter />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/classifieds" element={<Classifieds />} />
              <Route path="/social" element={<Social />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/reef-wizard" element={<ReefWizard />} />
              <Route path="/reef-wizard/tanks/new" element={<TankForm />} />
              <Route path="/reef-wizard/tanks/:tankId" element={<TankDisplay />} />
              <Route path="/reef-wizard/tanks/:tankId/chat" element={<ReefWizardChat />} />
              <Route path="/reef-wizard/chat" element={<ReefWizardChat />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:scientificName" element={<Articles />} />
              <Route path="/reefcyclopedia" element={<Reefcyclopedia />} />
              <Route path="/reefcyclopedia/:slug" element={<Reefcyclopedia />} />
              <Route path="/shopping-assistant" element={<ShoppingAssistant />} />
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/giveaway-rules" element={<GiveawayRules />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function App() {
  // Set a random background image for the #background element on load
  React.useEffect(() => {
    const randomNumber = Math.floor(Math.random() * 5) + 1;
    const backgroundElement = document.getElementById("background");
    if (backgroundElement) {
      backgroundElement.style.backgroundImage = `url(/background${randomNumber}-optimized.webp)`;
    }
  }, []);

  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <RouteSEO>
            <AppContent />
          </RouteSEO>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
